"use client";
import useFetch from "@/utils/fetch";
import {
  BettorRecentPick,
  OddVariant,
  RecentPick as RecentPickType,
  Sport,
  SportMetaData,
  SportType,
  formatSport,
} from "common";
import React from "react";
import Card from "../primitives/card";
import {
  ALL_ODD_OPTIONS,
  ALL_SPORT_OPTIONS_DETAILED,
} from "@/data/sports/options";
import Select from "../primitives/select";
import { SPORTS } from "@/utils/sports";
import Icon from "../primitives/icon";
import RecentPick, { RecentPickState } from "./recent-pick";
import NotFound from "../primitives/not-found";
import Link from "next/link";
import { useLocalDate } from "@/utils/hooks/local-date";
import { PickProvider } from "@/utils/providers/pick";

export default function RecentPicks({
  initialData,
}: {
  initialData: RecentPickType[];
}) {
  const initialDate = new Date();
  const { date: localDate } = useLocalDate();
  // const [selectedSport, setSelectedSport] = React.useState<Sport | "all">(
  //   "all"
  // );
  // const [selectedOdds, setSelectedOdds] = React.useState<OddVariant>("all");
  // const baseUrl: string = `${process.env.NEXT_PUBLIC_API_URL}/client/bettor-recent-picks/${username}/`;
  // const params: URLSearchParams = new URLSearchParams();
  // if (selectedSport !== "all") {
  //   params.append("sport", selectedSport);
  // }
  // if (selectedOdds !== "all") {
  //   params.append("odd", selectedOdds);
  // }
  // const request: string = `${baseUrl}?${params}`;
  // const {
  //   data: rawData,
  //   isLoading,
  //   isError,
  //   error,
  // } = useFetch<BettorRecentPick[] | undefined>(undefined, request, []);
  // const [data, setData] = React.useState<
  //   Record<string, Record<string, BettorRecentPick[]>> | undefined
  // >(undefined);
  if (!initialData) {
    return (
      <Card
        custom
        className="bg-black-gradient text-white px-6 py-8"
        header={{
          title: (
            <>
              <div className="text-headline-bold text-white">Recent Picks</div>
            </>
          ),
        }}
      >
        <div className="w-full h-full absolute top-0 right-0 bg-[url('/images/bettor/recent-picks-bg.png')] bg-no-repeat bg-right-top"></div>

        <div className="flex flex-col justify-center items-center gap-y-4">
          <NotFound
            label="no active picks"
            icon="ticket-buy"
            caption="When this user makes a pick you'll see it here."
          />
          <Link href="/" className="btn btn-sm btn-filled-primary">
            Start picking
          </Link>
        </div>
      </Card>
    );
  }
  const data: Record<
    string,
    Record<string, RecentPickType[]>
  > = React.useMemo(() => {
    return groupDataByDateAndSport(initialData);
  }, [initialData]);
  function formatPickDate(dateString: string) {
    const startOfDay = (date: Date) => {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    };

    const today = startOfDay(localDate || initialDate);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let pickDate = new Date(dateString);
    // if (pickDate < today) {
    //   return null;
    // }
    if (pickDate.getDay() === today.getDay()) {
      return "Today";
    } else if (pickDate.getDay() === tomorrow.getDay()) {
      return "Tomorrow";
    } else {
      return pickDate.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  }

  function groupDataByDateAndSport(
    data: RecentPickType[]
  ): Record<string, Record<string, RecentPickType[]>> {
    const sortedData = data.sort((a, b) => {
      const timeDiff =
        new Date(b.game.date_time_utc).getTime() -
        new Date(a.game.date_time_utc).getTime();

      if (timeDiff !== 0) return timeDiff;

      return a.game.global_game_id - b.game.global_game_id;
    });

    const groupedByDateAndSport = sortedData.reduce((acc, item) => {
      const formattedDateString: string | null = formatPickDate(
        item.game.date_time_utc
      );
      if (formattedDateString) {
        if (!acc[formattedDateString]) {
          acc[formattedDateString] = {};
        }

        if (!acc[formattedDateString][item.sport_type]) {
          acc[formattedDateString][item.sport_type] = [];
        }

        acc[formattedDateString][item.sport_type].push(item);
      }
      return acc;
    }, {} as Record<string, Record<string, RecentPickType[]>>);

    for (const date in groupedByDateAndSport) {
      for (const sport in groupedByDateAndSport[date]) {
        groupedByDateAndSport[date][sport].sort((a, b) => {
          const timeDiff =
            new Date(a.game.date_time_utc).getTime() -
            new Date(b.game.date_time_utc).getTime();

          if (timeDiff !== 0) return timeDiff;

          return a.game.global_game_id - b.game.global_game_id;
        });
      }
    }

    return groupedByDateAndSport;
  }

  // once raw data is in, sort and group it.
  // React.useEffect(() => {
  //   if (rawData) {
  //     setData(groupDataByDateAndSport(rawData));
  //   }
  // }, [rawData]);

  console.log("PICK DATA:", data);

  return (
    <Card
      custom
      className="bg-black-gradient text-white px-6 py-8"
      header={{
        title: (
          <>
            <div className="text-headline-bold text-white">Recent Picks</div>
          </>
        ),
        action: (
          <>
            {/* <div className="flex gap-6">
              <Select
                dark
                variant="card"
                label="Select Sport"
                value={selectedSport}
                onValueChange={(v) => setSelectedSport(v as Sport)}
                options={ALL_SPORT_OPTIONS_DETAILED}
              />
              <Select
                dark
                variant="card"
                label="All Odds"
                value={selectedOdds}
                onValueChange={(v) => setSelectedOdds(v as OddVariant)}
                options={ALL_ODD_OPTIONS}
              />
            </div> */}
          </>
        ),
      }}
    >
      <div className="w-full h-full absolute top-0 right-0 bg-[url('/images/bettor/recent-picks-bg.png')] bg-no-repeat bg-right-top"></div>
      {data && Object.keys(data).length ? (
        <div className="mt-6">
          {Object.entries(data).map(([date, sports], index) => {
            // console.log("unformatted date:", date);
            // const formattedDate = formatPickDate(date);
            // console.log("formatted date:", formattedDate);
            return (
              <React.Fragment key={`${date}-${index}`}>
                <DateDivider>{date}</DateDivider>
                {Object.entries(sports).map(([sport, picks]) => (
                  <SportGroup
                    key={sport}
                    sport={formatSport(Number(sport)) as Sport}
                  >
                    {picks.map(
                      (pick, i) =>
                        pick.game && (
                          <PickProvider
                            key={pick.game.global_game_id}
                            pick={pick}
                          >
                            <RecentPick state={RecentPickState.Profile} />
                          </PickProvider>
                        )
                    )}
                  </SportGroup>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center gap-y-4">
          <NotFound
            label="no active picks"
            icon="ticket-buy"
            caption="When this user makes a pick you'll see it here."
          />
          <Link href="/" className="btn btn-sm btn-filled-primary">
            Start picking
          </Link>
        </div>
      )}
    </Card>
  );
}

function DateDivider({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="justify-center items-center gap-6 flex w-full">
        <div className="grow shrink basis-0 h-px bg-[#3A3C3F]" />
        <div className="px-2 py-1 bg-[#303236] rounded-[96px] justify-center items-center gap-1 flex">
          <div className="text-center text-white text-label-lg-bold uppercase leading-[14px] tracking-tight">
            {children}
          </div>
        </div>
        <div className="grow shrink basis-0 h-px bg-[#3A3C3F]" />
      </div>
    </div>
  );
}

function SportGroup({
  sport,
  children,
}: {
  sport: Sport;
  children: React.ReactNode;
}) {
  const metadata: SportMetaData | undefined = SPORTS.find(
    (x) => x.id === sport
  );
  if (metadata) {
    return (
      <div className="mb-8">
        <div className="mb-4 text-white text-title-sm-medium flex gap-2 items-center">
          <Icon name={metadata.icon} className="fill-primary-500" />{" "}
          {metadata.label}
        </div>
        <div className="grid desktop-grid:grid-cols-2 gap-x-6 gap-y-4">
          {children}
        </div>
      </div>
    );
  }
  return <></>;
}
